import { Location } from '@angular/common';
// Angular modules.
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import {
	ActivatedRoute,
	Event,
	NavigationStart,
	Router,
	RouterEvent,
} from '@angular/router';
import * as Sentry from '@sentry/angular-ivy';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { CacheService } from 'app/services/cache.service';
import { ChatService } from 'app/services/chat.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { NetworkService } from 'app/services/network.service';
import { UserService } from 'app/services/user.service';
import { ServiceWorkerService } from 'modules/core/service-worker/service-worker.service';
import { ABExperimentsService } from 'modules/experiments/services/ABExperiments.service';
import * as moment from 'moment-timezone';
import { forkJoin, timer } from 'rxjs';
import { UnleashService } from 'shared/unleash/unleash.service';

import {
	IProfessionalProfile,
	IZkuProfessional,
} from './interfaces/professionalprofile.interface';
import { AuthenticationService } from './services/authentication.service';
import { IdWallService } from './services/id-wall.service';
import { StompService } from './services/stomp.service';
import { SubscriptionService } from './services/subscription.service';
import { TaxonomyService } from './services/taxonomy.service';
import { WePayOutService } from './services/wepayout.service';
import { ZkuService } from './services/zku.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
	@Output() newEventsFound: EventEmitter<any> = new EventEmitter<any>();

	// Show/hide header and notifications.
	showHeader = true;
	allowNotifications = false;
	isUserLogged = false;

	// Status.
	isMobile = false;
	isConnected = true;
	strCurrentOp: string;

	// User visibility.
	visibilityChecked = false;
	userIsVisible = false;
	receivedMessages: any;

	// Constructor.
	constructor(
		private zkuService: ZkuService,
		protected authentication: AuthenticationService,
		protected availabilities: AvailabilitiesService,
		protected cacheService: CacheService,
		protected chat: ChatService,
		protected events: EventsService,
		protected global: GlobalService,
		protected idWallService: IdWallService,
		protected network: NetworkService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected stompService: StompService,
		protected subscriptionService: SubscriptionService,
		protected taxonomy: TaxonomyService,
		protected unleash: UnleashService,
		protected user: UserService,
		protected wePayOutService: WePayOutService,
		private ABExperiments: ABExperimentsService,
		private SWService: ServiceWorkerService,
		private location: Location
	) {
		this.SWService.checkForUpdates();
		this.user.getAuthState().subscribe((user: firebase.User) => {
			this.isUserLogged = !!user;
			if (this.isUserLogged) {
				if ((window as any).clarity) {
					(window as any).clarity('identify', user.uid);
				}
				this.user
					.getProfessionalProfileData(user.uid)
					.subscribe((userData: IProfessionalProfile) => {
						if (userData) {
							const uImage =
								userData.imageUrl || this.global.getDefaultUserPicture();

							this.user.checkSubscriptionAndContactTiers().then((u) => {
								const zku = {
									...userData,
									imageUrl: uImage,
									profile: userData.profileUrl,
									name: userData.displayName,
									isPremium: u.hasActiveSubscription,
								};

								this.zkuService.setZku(zku);
								this.setUnleashUserContext(zku);
							});
						} else {
							this.authentication.logout();
						}
					});
			}
		});
	}

	// On init.
	async ngOnInit() {
		this.router.events.subscribe(() => {
			if (this.SWService.hasNewVersion) {
				this.location.historyGo(0);
			}
		});

		Sentry.addBreadcrumb({
			category: 'app',
			message: 'Angular started.',
			level: 'debug',
			type: 'debug',
			timestamp: new Date().toISOString() as any,
		});
		this.ABExperiments.startAllExperiments();

		this.isMobile = this.global.isMobileDevice();
		this.router.events.subscribe((actualRoute) => {
			this.modifyHeader(actualRoute);
			if (actualRoute instanceof NavigationStart) {
				const blockedNotificationRoutes = [
					'/adminlogin',
					'/login',
					'/logout',
					'/logoff',
					'/update',
					'/session',
					'account/payment',
				];
				this.allowNotifications =
					blockedNotificationRoutes.indexOf(actualRoute.url) === -1;

				if (actualRoute.url === '/logout') {
					this.chat.logout();
				}
			}
		});

		// prefetch the reasons, they are cached, so we won't need to go again on server for this session
		this.taxonomy.getTaxonomyList('reason').subscribe();

		// always reload
		if (this.user.getUserLocal()) {
			this.network.updateLocalData();
			forkJoin([
				this.subscriptionService.getSubscriptionList(),
				this.user.getProfileData(),
			]).subscribe(async ([subs, user]) => {
				// subscription stuff
				this.subscriptionService.addToDataLayer(subs);
				// user stuff
				localStorage.setItem('zkisvisible', user.isVisible.toString());
				this.userIsVisible = user.isVisible;
				this.visibilityChecked = user.isVisible;

				// Start chat --> we do this here since we need to be logged in
				if (this.allowNotifications && (await this.user.isLoggedIn())) {
					await this.initChat();
				}
			});

			// subscribe for events updates
			this.wePayOutService.responseWePayOutValidation(
				this.user.getUserLocal().id
			);
			this.idWallService.responseCnpjValidation(this.user.getUserLocal().id);
			this.stompService.connectToAppointmentsTopic(this.user.getUserLocal().id);

			if (this.user.getUserLocal().isPremium === undefined) {
				this.user.checkSubscriptionAndContactTiers().then((u) => {
					this.user.getUserLocal().isPremium = u.hasActiveSubscription;
				});
			}

			this.setUnleashUserContext(this.user.getUserLocal());
		}

		// should we update the calendar cache?
		const cacheWeek = localStorage.getItem('dbEventsWeek');
		if (cacheWeek) {
			const update = Number(cacheWeek) !== moment.utc().isoWeek();
			if (update) {
				// clear cached events
				this.cacheService.deleteStore('events');
				// get events
				this.events.updateLocalEvents({ setWeek: true });
			}
		}

		// Timer for Keep-Alive signal.
		const keepAliveTimer = timer(0, 60000);
		keepAliveTimer.subscribe(() => {
			if (this.user.getUserLocal()) {
				this.network.keepAlive();
			}
		});

		// Timer for check if is connected.
		const connectionTimer = timer(0, 5000);
		connectionTimer.subscribe(
			() => (this.isConnected = this.network.checkConnectivity())
		);

		// Timer for user's visibility status.
		const visibleTimer = timer(0, 10000);
		visibleTimer.subscribe(() => {
			if (this.user.getUserLocal()) {
				this.syncOnlineStatus();
			}
		});

		// Listeners to connection on/off.
		window.addEventListener('online', this.connectionOn);
		window.addEventListener('offline', this.connectionOff);
	}

	private setUnleashUserContext(zku: IZkuProfessional): void {
		const userContext: any = {
			userId: zku.id,
			properties: {
				email: zku.email,
				type: zku.mainType,
				isPremium: zku.isPremium,
			},
		};
		this.unleash.updateContext(userContext);
	}

	// When connection is OK.
	connectionOn(): void {
		this.isConnected = true;
	}

	// When offline.
	connectionOff(): void {
		this.isConnected = false;
	}

	async initChat() {
		await this.chat.initChat();
		if (this.chat.browserNotificationsIs('granted')) {
			await this.chat.createInbox();
		}
	}

	// Set if user is visible (online).
	public changeUserVisibility(isOnline: boolean): void {
		this.user.setOnlineStatus(isOnline).subscribe(() => {
			this.userIsVisible = isOnline;
		});
	}

	// Sync doctor's data.
	private async syncOnlineStatus() {
		if ((await this.user.isLoggedIn()) && this.network.checkConnectivity()) {
			this.userIsVisible = this.user.getOnlineStatus();
			this.visibilityChecked = true;
		}
	}

	// Change ID of body element.
	changeBodyId(newId: string): void {
		// Default complement.
		const def = 'Zenklub';

		// Format new id.
		if (newId) {
			newId = newId.replace('/', '');
			newId = newId.split(' ')[0];
			newId = newId.charAt(0).toUpperCase() + newId.slice(1).toLowerCase();
			newId = 'body' + (newId ? newId : def);
		} else {
			newId = 'body' + def;
		}

		// Change id.
		const body: HTMLElement = document.getElementsByTagName('body')[0];
		body.id = newId;
	}

	// Show or hide header according to active route.
	modifyHeader(location: Event) {
		// Pages without header.
		const pagesWithoutHeader: string[] = this.global.getPagesWithoutHeader();

		if (location instanceof RouterEvent) {
			// Verify if header must be hidden.
			let mustShow = true;
			const arValues: string[] = location.url.split('/');

			arValues.forEach((item) => {
				pagesWithoutHeader.forEach((p) => {
					if (item && item.toLowerCase().startsWith(p)) {
						mustShow = false;
					}
				});
			});
			this.showHeader = mustShow;

			// Change body ID.
			this.changeBodyId(location.url);
		}
	}

	// Force to hide header.
	public forceHideHeader(): void {
		this.showHeader = false;
	}

	// Force to hide notifications.
	public toggleNotificationsVisibility(isAllowed = false): void {
		this.allowNotifications = isAllowed;
	}

	// When content window is resized.
	public onResize(): void {
		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();
	}
}
